import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../services/api';
import { AnalyticsShell, StatCard } from '../components/analytics/AnalyticsShell';
import { Stagger, fadeUp, AnimatedBar } from '../components/analytics/anim';

export const Route = createFileRoute('/analytics/')({
  component: AnalyticsList,
  validateSearch: (search: Record<string, unknown>): { video?: string } =>
    typeof search.video === 'string' && search.video ? { video: search.video } : {},
});

interface VideoRow {
  shareToken: string;
  /** 'video' or 'assessment' — a standalone assessment is a first-class row
   *  here, not something hidden because it has no video attached. */
  kind?: 'video' | 'assessment';
  conversationId: string;
  title: string | null;
  ownerEmail: string | null;
  views: number;
  uniqueViewers: number;
  completes: number;
  avgPercentViewed: number;
  lastViewedAt: string | null;
  isActive: boolean;
  createdAt: string;
}

function pct(n: number, d: number) {
  return d > 0 ? Math.round((n / d) * 100) : 0;
}

function ago(iso: string) {
  const d = new Date(iso).getTime();
  const s = Math.max(0, (Date.now() - d) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function AnalyticsList() {
  const { video } = Route.useSearch();
  const [videos, setVideos] = useState<VideoRow[] | null>(null);
  const [admin, setAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setVideos(null);
    api
      .get('/analytics/dashboard/videos', { params: video ? { conversationId: video } : undefined })
      .then((r) => {
        setVideos(r.data.videos ?? []);
        setAdmin(Boolean(r.data.admin));
      })
      .catch((e) => setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load'));
  }, [video]);

  const totals = videos?.reduce(
    (a, v) => ({
      views: a.views + v.views,
      unique: a.unique + v.uniqueViewers,
      completes: a.completes + v.completes,
    }),
    { views: 0, unique: 0, completes: 0 },
  );

  return (
    <AnalyticsShell>
      <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {video ? videos?.[0]?.title || 'Video Analytics' : 'Video Analytics'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {video
              ? 'Every link published for this video. Each link keeps its own analytics.'
              : 'Each card is one published link — a video or a standalone assessment — that people have opened.'}
          </p>
        </div>
        {video ? (
          <Link
            to="/analytics"
            className="rounded-lg border border-border/60 bg-card px-3.5 py-2 text-sm font-medium hover:border-primary/50 hover:text-primary"
          >
            All videos →
          </Link>
        ) : admin ? (
          <Link
            to="/analytics/actors"
            className="rounded-lg border border-border/60 bg-card px-3.5 py-2 text-sm font-medium hover:border-primary/50 hover:text-primary"
          >
            Learners →
          </Link>
        ) : null}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {!videos && !error && <div className="text-sm text-muted-foreground">Loading…</div>}

      {videos && videos.length === 0 && (
        <div className="rounded-xl border border-border/60 bg-card py-16 text-center">
          <div className="text-sm font-medium">{video ? 'No links published yet' : 'No views captured yet'}</div>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            {video
              ? 'Publish this video from the studio and its links will appear here.'
              : "Videos appear here once they've been watched. Share a video and its analytics will start flowing in."}
          </p>
        </div>
      )}

      {videos && videos.length > 0 && totals && (
        <>
          <Stagger className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard label={video ? 'Links' : 'Published'} value={videos.length} accent />
            <StatCard label="Total views" value={totals.views.toLocaleString()} />
            <StatCard label="Unique viewers" value={totals.unique.toLocaleString()} />
            <StatCard label="Avg completion" value={`${pct(totals.completes, totals.views)}%`} />
          </Stagger>

          <Stagger className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {videos.map((v, i) => {
              const completion = pct(v.completes, v.views);
              return (
                <motion.div key={v.shareToken} variants={fadeUp} whileHover={{ y: -4 }} transition={{ type: 'spring', stiffness: 380, damping: 26 }}>
                  <Link
                    to="/analytics/$shareToken"
                    params={{ shareToken: v.shareToken }}
                    className="group block h-full rounded-xl border border-border/60 bg-card p-5 transition-shadow hover:border-primary/50 hover:shadow-xl hover:shadow-primary/10"
                  >
                    {v.kind === 'assessment' && (
                      <span className="mb-1.5 inline-block rounded-md bg-primary/10 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.1em] text-primary">
                        Assessment
                      </span>
                    )}
                    <div className="line-clamp-2 min-h-[2.5rem] font-medium leading-snug group-hover:text-primary">
                      {v.title || (v.kind === 'assessment' ? '(untitled assessment)' : '(untitled video)')}
                    </div>
                    <div className="mb-3 mt-0.5 truncate text-xs text-muted-foreground">
                      {admin && !video && v.ownerEmail ? `${v.ownerEmail} · ` : ''}
                      <span className="font-mono">{v.shareToken.slice(0, 8)}…</span> · published {ago(v.createdAt)}
                      {!v.isActive && <span className="ml-1.5 text-destructive">revoked</span>}
                    </div>

                    <div className="grid grid-cols-3 gap-2 border-t border-border/60 pt-3">
                      <CardMetric label="Views" value={v.views} />
                      <CardMetric label="Unique" value={v.uniqueViewers} />
                      <CardMetric label="Avg %" value={`${v.avgPercentViewed ?? 0}%`} />
                    </div>

                    <div className="mt-4">
                      <div className="mb-1 flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Completion</span>
                        <span className="font-medium tabular-nums">{completion}%</span>
                      </div>
                      <AnimatedBar pct={completion} delay={0.15 + i * 0.04} />
                    </div>

                    <div className="mt-4 text-xs text-muted-foreground">
                      {v.lastViewedAt ? `Last viewed ${ago(v.lastViewedAt)}` : 'Not viewed yet'}
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </Stagger>
        </>
      )}
    </AnalyticsShell>
  );
}

function CardMetric({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}
